import {
  DOCUMENT_EXTENSIONS,
  DOCUMENT_MIME_TYPES,
  MAX_FILES_PER_UPLOAD,
  MAX_UPLOAD_FILE_SIZE,
  ZIP_MIME_TYPES,
} from "./uploadConstants";

type FileUploadStatus = "pending" | "uploading" | "done" | "error";

export interface FileEntry {
  file: File;
  status: FileUploadStatus;
  progress: number;
  error?: string;
}

export type UploadFileValidationError = "invalid_type" | "file_too_large";

export function getExtension(filename: string): string {
  const dot = filename.lastIndexOf(".");
  if (dot === -1) return "";
  return filename.slice(dot + 1).toLowerCase();
}

export function getBasename(path: string): string {
  const slash = path.lastIndexOf("/");
  return slash === -1 ? path : path.slice(slash + 1);
}

export function isValidDocumentExtension(filename: string): boolean {
  return DOCUMENT_EXTENSIONS.has(getExtension(filename));
}

export function isDocumentMimeType(mimeType: string): boolean {
  return (DOCUMENT_MIME_TYPES as readonly string[]).includes(mimeType);
}

export function isZipFile(file: File): boolean {
  if (ZIP_MIME_TYPES.has(file.type)) return true;
  return file.name.toLowerCase().endsWith(".zip");
}

export function validateUploadFile(
  file: Pick<File, "type" | "size">,
): UploadFileValidationError | null {
  if (!isDocumentMimeType(file.type)) return "invalid_type";
  if (file.size > MAX_UPLOAD_FILE_SIZE) return "file_too_large";
  return null;
}

export interface UploadLimitResult<T> {
  entries: T[];
  isOverLimit: boolean;
}

export function appendWithinUploadLimit<T>(current: T[], incoming: T[]): UploadLimitResult<T> {
  if (current.length + incoming.length <= MAX_FILES_PER_UPLOAD) {
    return { entries: [...current, ...incoming], isOverLimit: false };
  }
  const remaining = MAX_FILES_PER_UPLOAD - current.length;
  return {
    entries: remaining > 0 ? [...current, ...incoming.slice(0, remaining)] : current,
    isOverLimit: true,
  };
}

export interface ZipPartition {
  zipFiles: File[];
  regularFiles: File[];
}

export function partitionZipFiles(files: File[]): ZipPartition {
  return {
    zipFiles: files.filter(isZipFile),
    regularFiles: files.filter((file) => !isZipFile(file)),
  };
}
